import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import { FileUploader } from 'components/FileUploader';
import { type OnCompleteStatus } from 'components/FileUploader/Modal/FileUploadModal';
import { Button, ModalActionButtons, useNotification } from 'components/ui';
import {
  useNotifyInspectionImageUploadErrorMutation,
  useNotifyInspectionImageUploadSuccessMutation,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { IMAGE_AND_METADATA_UPLOAD_OPTIONS } from 'utils/constants';
import { useGetIngestUploadUrls } from 'utils/ingest';

const uploadDescription = i18n.t(
  'inspection.roboticsUploadDescription',
  'Upload a zipped (.zip) payload to be handled and processed by a robotics job.'
);
const uploadLabel = i18n.t('inspection.roboticsPackageLabel', 'Robotics package');

interface RoboticsUploadProps {
  inspectionId: string;
  onComplete: () => void;
  onBack: () => void;
}

export const RoboticsUpload: React.FunctionComponent<RoboticsUploadProps> = ({
  inspectionId,
  onComplete: onSuccessCallback,
  onBack,
}) => {
  const { t } = useTranslation();
  const notification = useNotification();
  const { customer } = useAccountContext();
  const organizationId = customer?.id;

  // So long as the file ends in the expected file type we should be good
  const isValidFilePath = (filename: string) => {
    return filename.toLowerCase().endsWith('.zip');
  };

  const [notifySuccess] = useNotifyInspectionImageUploadSuccessMutation();
  const [notifyError] = useNotifyInspectionImageUploadErrorMutation();
  const { cancelTokenSource, getIngestUploadUrls } = useGetIngestUploadUrls();

  const getUploadUrls = async (filePaths: any) => {
    if (!organizationId) {
      return undefined;
    }

    try {
      const { items } = await getIngestUploadUrls({
        recordId: inspectionId,
        dataType: IMAGE_AND_METADATA_UPLOAD_OPTIONS.ROBOTICS_UPLOAD,
        files: filePaths || [],
      });

      return items;
    } catch (e) {
      return undefined;
    }
  };

  const onUploadComplete = () => {
    onSuccessCallback();
    notification.open({
      message: t('inspection.dataProcessingTitle', 'Inspection data currently processing.'),
      description: t(
        'inspection.dataProcessingDescription',
        'You will receive a notification when your inspection has been processed.'
      ),
    });
  };

  const onComplete = ({ status }: { status: OnCompleteStatus }) => {
    if (status === 'CANCEL') {
      cancelTokenSource.cancel('Operation canceled by the user.');
      return;
    }
    if (status === 'SUCCESS') {
      onUploadComplete();
      notifySuccess({
        variables: {
          input: {
            inspectionId: inspectionId,
            message: t(
              'inspection.uploadSuccessMessage',
              '{{dataType}} has been successfully uploaded to your inspection with id "{{inspectionId}}".',
              {
                dataType: uploadLabel,
                inspectionId,
              }
            ),
          },
        },
      });
    }
    if (status === 'ERROR') {
      notifyError({
        variables: {
          input: {
            inspectionId: inspectionId,
            message: t(
              'inspection.uploadErrorMessage',
              'An error occurred while uploading {{dataType}} to your inspection with id "{{inspectionId}}".',
              {
                dataType: uploadLabel,
                inspectionId,
              }
            ),
          },
        },
      });
    }
  };

  return (
    <div>
      <p>{uploadDescription}</p>
      <ModalActionButtons>
        <Button data-testid="inspection-upload-cancel-btn" onClick={onBack}>
          {t('common.back', 'Back')}
        </Button>
        <FileUploader
          id="robotics-data-upload"
          label={t('inspection.uploadFile', 'Upload File')}
          acceptedFileTypes=".zip"
          multiple={false}
          directory={false}
          validation={isValidFilePath}
          onFileComplete={() => {}}
          onComplete={onComplete}
          action={getUploadUrls}
          multipartProps={{
            recordId: inspectionId,
            outputDataType: IMAGE_AND_METADATA_UPLOAD_OPTIONS.ROBOTICS_UPLOAD,
          }}
        />
      </ModalActionButtons>
    </div>
  );
};
